import { type DueNow, getReplenishmentDue, getReplenishmentMaterial, getReplenishmentObjectiveCounts, getReplenishmentPolicy, getReplenishmentSku, getReplenishmentView, type InstrumentRow, listReplenishmentObjectives, type MaterialDetail, type ObjectiveCount, previewSaveReplenishmentInstruments, refreshReplenishmentRun, type RequirementRow, type SavedObjective, type SavePreview, type SaveResult, type ViewResponse } from '@api/replenishment-engine';
import { Alert, Badge, Button, Group, Loader, NumberInput, Stack, Tabs, Text, Tooltip } from '@mantine/core';
import { ArrowsClockwise, GearSix, ListChecks } from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react';
import { useSearchParams } from 'react-router';

import ScheduleButton from '@components/ui/ScheduleAlertDialog/ScheduleButton';

import ApprovalSheet from './ApprovalSheet';
import CalendarView from './CalendarView';
import { addDaysKey, dayLabel, localDateKey } from './dates';
import CellWindow from './drilldowns';
import classes from './engine.module.css';
import { describePeriodRule, requestFromSaved } from './objective';
import SetupWizard from './SetupWizard';
import MaterialWindow from './MaterialWindow';
import SkuWindow, { type SkuTab } from './SkuWindow';
import DipTable from './DipTable';
import PivotPanel from './PivotPanel';
import SaveReviewSheet from './SaveReviewSheet';
import { type CellTarget } from './tables';
import TrackPanel from './TrackPanel';

const readError = (e: unknown): string => {
    const err = e as { error?: { message?: string }; message?: string };
    return err?.error?.message ?? err?.message ?? 'Something went wrong';
};

type Pivot = 'PRODUCTION' | 'MATERIAL' | 'FG' | 'TRANSFER';
type Panel = 'objective' | Pivot | 'track';

const PIVOT: Record<Pivot, { title: string; sub: string; party: [string, string]; types: InstrumentRow['type'][] }> = {
    PRODUCTION: { title: 'Production orders', sub: 'runs to start, per factory', party: ['factory', 'factories'], types: ['WORK_ORDER'] },
    MATERIAL: { title: 'RM / PM orders', sub: 'purchase orders to place, per vendor', party: ['vendor', 'vendors'], types: ['MATERIAL_PO'] },
    FG: { title: 'FG purchase orders', sub: 'finished goods to buy, per vendor', party: ['vendor', 'vendors'], types: ['FG_PO'] },
    TRANSFER: { title: 'Transfers', sub: 'STNs to raise, per lane', party: ['lane', 'lanes'], types: ['TRANSFER'] },
};
const daysBetween = (from: string, to: string): number => Math.round((new Date(`${to}T00:00:00`).getTime() - new Date(`${from}T00:00:00`).getTime()) / 86400000);

/**
 * The page: three pivots over what the latest run proposes (production orders, purchase
 * orders, transfers — each per party, within the next N days), the brand's objectives as
 * tabs, and settings. Every approval goes through the review sheet, which shows the whole
 * chain and the documents it will create.
 */
const ObjectivesView: FC = () => {
    const today = localDateKey();
    const [objectives, setObjectives] = useState<SavedObjective[] | null>(null);
    const [active, setActive] = useState<string | null>(null);
    const [view, setView] = useState<ViewResponse | null>(null);
    const [due, setDue] = useState<DueNow | null>(null);
    const [counts, setCounts] = useState<ObjectiveCount[]>([]);
    const [horizonCap, setHorizonCap] = useState<number>(90);
    const [withinDays, setWithinDays] = useState<number>(30);
    /** Every proposed row of the run, once; the period strip filters it in memory. */
    const [all, setAll] = useState<InstrumentRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [skuDetail, setSkuDetail] = useState<RequirementRow[] | null>(null);
    const [skuTab, setSkuTab] = useState<SkuTab>('story');
    const [materialDetail, setMaterialDetail] = useState<MaterialDetail | null>(null);
    const [cell, setCell] = useState<CellTarget | null>(null);
    const [approvalIds, setApprovalIds] = useState<string[] | null>(null);
    const [notice, setNotice] = useState<string | null>(null);

    // /replenishment?tab=plan&objective=<id> — a Home plan card opens its objective directly.
    const [searchParams, setSearchParams] = useSearchParams();
    const wantedObjective = useRef<string | null>(searchParams.get('objective'));
    // Track / a pivot card lives in the URL too (`?panel=`), for the same reason as the objective.
    const panelParam = searchParams.get('panel');
    const panel: Panel = panelParam === 'track' || (panelParam && panelParam in PIVOT) ? (panelParam as Panel) : 'objective';
    const setPanel = useCallback(
        (next: Panel) =>
            setSearchParams(
                (prev) => {
                    const out = new URLSearchParams(prev);
                    if (next === 'objective') out.delete('panel');
                    else out.set('panel', next);
                    return out;
                },
                { replace: true },
            ),
        [setSearchParams],
    );
    const loadObjectives = useCallback(async () => {
        try {
            const list = await listReplenishmentObjectives();
            setObjectives(list);
            const onPage = list.filter((o) => o.onHome);
            const wanted = wantedObjective.current;
            wantedObjective.current = null;
            setActive((cur) =>
                wanted && onPage.some((o) => o.id === wanted)
                    ? wanted
                    : cur && onPage.some((o) => o.id === cur)
                      ? cur
                      : (onPage[0]?.id ?? null),
            );
        } catch (e) {
            setObjectives([]);
            setError(readError(e));
        }
    }, []);
    const loadPolicy = useCallback(async () => {
        try {
            const p = await getReplenishmentPolicy();
            setHorizonCap(Math.max(1, p.latestHorizonDays ?? p.horizonDays ?? 90));
        } catch {
            /* keep the default */
        }
    }, []);
    const loadDue = useCallback(async () => {
        try {
            // One read of everything the run proposed, as far as it looked; the strip narrows it.
            const d = await getReplenishmentDue(horizonCap);
            setDue(d);
            setAll([...d.dueToday, ...d.comingUp]);
        } catch {
            setDue(null);
            setAll([]);
        }
        try {
            setCounts(await getReplenishmentObjectiveCounts());
        } catch {
            setCounts([]);
        }
    }, [horizonCap]);
    useEffect(() => {
        void loadObjectives();
        void loadPolicy();
    }, [loadObjectives, loadPolicy]);
    useEffect(() => {
        void loadDue();
    }, [loadDue]);
    useEffect(() => {
        if (objectives && objectives.filter((o) => o.onHome).length === 0) setSettingsOpen(true);
        // Only on the first load: an empty page opens the setup; closing it leaves the start button.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objectives === null]);

    const current = useMemo(() => objectives?.find((o) => o.id === active) ?? null, [objectives, active]);
    // The open objective lives in the URL too, so a scheduled email of its tables reopens
    // this objective rather than the first one.
    useEffect(() => {
        if (!active || searchParams.get('objective') === active) return;
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.set('objective', active);
                return next;
            },
            { replace: true },
        );
    }, [active, searchParams, setSearchParams]);

    const loadView = useCallback(async () => {
        if (!current) {
            setView(null);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            setView(await getReplenishmentView(requestFromSaved(current, today)));
        } catch (e) {
            setError(readError(e));
        } finally {
            setLoading(false);
        }
    }, [current, today]);
    useEffect(() => {
        void loadView();
    }, [loadView]);

    // The SKU window under an objective tells the story of the dips in that objective's
    // window, not the SKU's whole horizon. The curve rides on the first dip's row, so it is
    // carried over when that row is outside the window.
    const scopeToObjective = useCallback(
        (rows: RequirementRow[], productId: string): RequirementRow[] => {
            const dips = new Set((view?.skus ?? []).filter((s) => s.productId === productId).map((s) => `${s.productId}|${s.seq}`));
            if (dips.size === 0) return rows;
            const scoped = rows.filter((r) => dips.has(`${r.productId}|${r.seq}`));
            if (scoped.length === 0) return rows;
            if (scoped.some((r) => r.series)) return scoped;
            const series = rows.find((r) => r.series)?.series ?? null;
            return series ? scoped.map((r, i) => (i === 0 ? { ...r, series } : r)) : scoped;
        },
        [view],
    );
    const reloadAll = useCallback(async () => {
        await Promise.all([loadView(), loadDue(), loadPolicy()]);
        if (skuDetail) setSkuDetail(scopeToObjective(await getReplenishmentSku(skuDetail[0].productId).catch(() => skuDetail), skuDetail[0].productId));
        if (materialDetail) setMaterialDetail(await getReplenishmentMaterial(materialDetail.materialId, materialDetail.siteId).catch(() => materialDetail));
        if (cell) {
            // The same dip, re-read: its head row and the parts the cell was opened on.
            const rows = await getReplenishmentSku(cell.row.productId).catch(() => [] as RequirementRow[]);
            const dip = rows.filter((r) => r.seq === cell.row.seq).sort((a, b) => a.part - b.part);
            const wanted = new Set(cell.parts.map((p) => p.part));
            const parts = cell.parts.length === dip.filter((r) => r.part > 0).length ? dip.filter((r) => r.part > 0) : dip.filter((r) => wanted.has(r.part));
            if (dip[0]) setCell({ ...cell, row: dip[0], parts });
        }
    }, [loadView, loadDue, loadPolicy, skuDetail, materialDetail, cell, scopeToObjective]);

    const refresh = useCallback(async () => {
        setRefreshing(true);
        setError(null);
        try {
            await refreshReplenishmentRun();
            await reloadAll();
        } catch (e) {
            setError(readError(e));
        } finally {
            setRefreshing(false);
        }
    }, [reloadAll]);

    // Every approve button on the page opens the review sheet.
    const approve = useCallback((ids: string[]) => {
        if (ids.length) setApprovalIds(ids);
    }, []);

    // Save freezes the lines as they stand under the current objective and its period: no
    // document, no pin, nothing the next run reads. They come back under Track with actuals.
    // Every Save opens the review sheet: each line against what this objective already saved
    // (this period, or an earlier day the run rolled from), with a tick to leave lines out.
    const [saveReview, setSaveReview] = useState<SavePreview | null>(null);
    const period = useMemo(() => ({ start: view?.periodStart ?? null, end: view?.periodEnd ?? null }), [view]);
    const savedNotice = useCallback(
        (result: SaveResult) => {
            const when = period.start && period.end ? ` for ${dayLabel(period.start)} → ${dayLabel(period.end)}` : '';
            const bits = [`Saved ${result.saved.length} line${result.saved.length === 1 ? '' : 's'}${when}`];
            if (result.kept.length) bits.push(`${result.kept.length} already saved, kept`);
            if (result.replaced.length) bits.push(`${result.replaced.length} replaced`);
            if (result.withdrawn.length) bits.push(`${result.withdrawn.length} withdrawn`);
            if (result.refused.length) bits.push(`${result.refused.length} skipped (${result.refused[0].reason})`);
            setNotice(`${bits.join(' · ')}. Find them under Track.`);
        },
        [period],
    );
    const save = useCallback(
        async (ids: string[]) => {
            if (!ids.length) return;
            setError(null);
            try {
                setSaveReview(await previewSaveReplenishmentInstruments(ids, current?.id, period));
            } catch (e) {
                setError(readError(e));
            }
        },
        [current, period],
    );

    const openMaterial = useCallback(async (materialId: string, siteId: string) => {
        try {
            setMaterialDetail(await getReplenishmentMaterial(materialId, siteId));
        } catch (e) {
            setError(readError(e));
        }
    }, []);
    const openSku = useCallback(
        async (productId: string, tab: SkuTab = 'story') => {
            try {
                setSkuTab(tab);
                setSkuDetail(scopeToObjective(await getReplenishmentSku(productId), productId));
            } catch (e) {
                setError(readError(e));
            }
        },
        [scopeToObjective],
    );
    const subtitle = useMemo(() => {
        if (!view || !current) return '';
        const when = view.runAt ? new Date(view.runAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : view.runDate;
        const periodText = current.periodRule.kind === 'LEAD_TIME' ? describePeriodRule(current.periodRule) : `${dayLabel(view.periodStart)} → ${dayLabel(view.periodEnd)}`;
        return `${periodText} · ${view.preview ? 'preview, nothing written' : `as of run ${when}`}`;
    }, [view, current]);

    const onPage = (objectives ?? []).filter((o) => o.onHome);
    const activeObjective = onPage.find((o) => o.id === active) ?? null;
    // The cards speak the open objective's language: its own lines (a SKU objective's are the
    // lines that fix its dips), over its own period. For a SKU objective that runs to the last
    // place-by its dips need; for a document objective, to its period end. The strip can narrow
    // it, never widen it. With no objective open, the cards read the whole run.
    const scope = useMemo<InstrumentRow[]>(() => {
        if (!view || !current) return all;
        if (view.shape !== 'SKU') return view.instruments;
        const seen = new Set<string>();
        return view.skus.flatMap((s) => s.instruments).filter((i) => i.status === 'PROPOSED' && (seen.has(i.id) ? false : (seen.add(i.id), true)));
    }, [view, current, all]);
    const latestDay = useMemo(() => {
        const lastPlace = Math.max(1, ...scope.map((r) => daysBetween(today, r.placeBy) + 1));
        if (!view || !current) return lastPlace;
        if (view.shape === 'SKU') return lastPlace;
        return view.periodEnd ? Math.max(1, daysBetween(today, view.periodEnd) + 1) : lastPlace;
    }, [scope, view, current, today]);
    // A new objective opens at its full period; the strip only narrows from there.
    useEffect(() => {
        setWithinDays(latestDay);
    }, [latestDay]);
    const windowEnd = addDaysKey(today, withinDays - 1);
    const proposed = useMemo(() => scope.filter((r) => r.placeBy <= windowEnd), [scope, windowEnd]);
    // A card per kind of line the objective (or run) has at all: a buy-only brand sees no production or material card.
    const present = useMemo(() => (Object.keys(PIVOT) as Pivot[]).filter((key) => scope.some((i) => PIVOT[key].types.includes(i.type))), [scope]);
    const byPivot = useMemo(() => {
        const out = {} as Record<Pivot, { rows: InstrumentRow[]; parties: number; skus: number; units: number; next: string | null }>;
        for (const key of Object.keys(PIVOT) as Pivot[]) {
            const rows = proposed.filter((i) => PIVOT[key].types.includes(i.type));
            const every = scope.filter((i) => PIVOT[key].types.includes(i.type));
            out[key] = {
                rows,
                parties: new Set(rows.map((r) => (r.type === 'TRANSFER' ? `${r.fromFacilityId}>${r.toFacilityId}` : r.vendorId ?? r.fromFacilityId ?? 'none'))).size,
                skus: new Set(rows.map((r) => r.productId)).size,
                units: rows.reduce((sum, r) => sum + r.qty, 0),
                // The first place-by past the window: what the card points at when it is empty.
                next: every.filter((r) => r.placeBy > windowEnd).map((r) => r.placeBy).sort()[0] ?? null,
            };
        }
        return out;
    }, [proposed, scope, windowEnd]);
    const choices = due?.choicesPending.length ?? 0;
    const runStamp = due?.runAt ? new Date(due.runAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'no run yet';

    /** An objective tab that is document-shaped: the same pivot table as a card, over the objective's own rows. */
    const pivotPanel = (key: Pivot, source: { rows: InstrumentRow[]; subtitle: string }): React.ReactNode => (
        <PivotPanel kind={key} rows={source.rows} skus={view?.skus ?? []} blocked={view?.blocked ?? []} idleSkus={view?.idleSkus ?? []} idleMaterials={view?.idleMaterials ?? []} windowStart={view?.periodStart ?? null} windowEnd={view?.periodEnd ?? null} title={PIVOT[key].title} subtitle={source.subtitle} onOpenSku={(productId, tab) => void openSku(productId, tab)} onOpenMaterial={(materialId, siteId) => void openMaterial(materialId, siteId)} onApprove={approve} onSave={(ids) => void save(ids)} onChanged={reloadAll} factories={view?.factories} />
    );

    const table = (): React.ReactNode => {
        if (!current) return null;
        if (!view && loading) return <Loader size="sm" />;
        if (!view) return null;
        if (view.shape === 'SKU') return <DipTable rows={view.skus} loading={loading} title={current.sentence} subtitle={subtitle} windowStart={view.periodStart} windowEnd={view.periodEnd} onOpenSku={(productId, tab) => void openSku(productId, tab)} onApprove={approve} onSave={(ids) => void save(ids)} onChanged={reloadAll} />;
        // A document-shaped objective is one of the pivots with its own window: same table,
        // same parties, same numbers as the card.
        const source = { rows: view.instruments, subtitle: `${current.sentence} · ${subtitle}` };
        if (view.shape === 'PRODUCTION') return pivotPanel('PRODUCTION', source);
        if (view.shape === 'MATERIAL') return pivotPanel(view.instruments.some((i) => i.type === 'TRANSFER') && !view.instruments.some((i) => i.type === 'MATERIAL_PO') ? 'TRANSFER' : 'MATERIAL', source);
        return pivotPanel('FG', source);
    };

    return (
        <Stack gap="md" px="md" pb="xl">
            <Group className={classes.bar} justify="space-between" align="center">
                <Stack gap={0}>
                    <Text fw={700}>Replenishment</Text>
                    <Text size="xs" c="dimmed">
                        as of run {runStamp} · runs after each daily sync
                    </Text>
                </Stack>
                <Group gap="xs" align="center">
                    <Tooltip label="Run the engine now with today's data. Proposed rows are rebuilt; approved ones are untouched." withArrow>
                        <Button size="xs" variant="default" leftSection={refreshing ? <Loader size={12} /> : <ArrowsClockwise size={14} />} onClick={() => void refresh()} disabled={refreshing}>
                            Refresh
                        </Button>
                    </Tooltip>
                    <Button size="xs" variant={panel === 'track' ? 'light' : 'default'} leftSection={<ListChecks size={14} />} onClick={() => setPanel(panel === 'track' ? 'objective' : 'track')}>
                        Track
                    </Button>
                    {activeObjective && panel === 'objective' && (
                        <ScheduleButton
                            matchOn="objectiveId"
                            title={`Schedule: ${activeObjective.sentence}`}
                            description="The objective is re-read on this schedule against the latest run. Its open lines show as a card on Home; opening it brings you to this objective."
                            autoSave={{ label: 'Save the plan on every run', description: 'Each run saves its open lines for this objective, as Save does: nothing is approved and no document is created. Numbers someone fixed by hand are kept for their period, and a run that moved to a new day replaces its earlier save.' }}
                            alert={{
                                alertType: 'REPLENISHMENT_OBJECTIVE_DUE',
                                alertLabel: `Plan · ${activeObjective.sentence}`,
                                // The link back here is the type's default (action.constants), filled from this context.
                                context: {
                                    objectiveId: activeObjective.id,
                                    sentence: activeObjective.sentence,
                                },
                            }}
                />
                    )}
                    <Button size="xs" variant="default" leftSection={<GearSix size={14} />} onClick={() => setSettingsOpen(true)}>
                        Settings
                    </Button>
                </Group>
            </Group>

            {error && (
                <Alert color="red" withCloseButton onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {notice && (
                <Alert color="grape" withCloseButton onClose={() => setNotice(null)}>
                    {notice}
                </Alert>
            )}

            <Group gap="sm" align="center" wrap="wrap" className={classes.periodBar}>
                <Text size="sm" fw={600}>
                    To place in the next
                </Text>
                <NumberInput size="xs" w={110} min={1} max={latestDay} value={withinDays} onChange={(v) => setWithinDays(Math.min(latestDay, Math.max(1, Number(v) || 1)))} suffix=" days" />
                <Text size="xs" c="dimmed">
                    {current && view ? `by place-by date, for “${current.sentence}” · ${view.shape === 'SKU' ? `its dips need their last line placed ${latestDay} days out` : `its period runs ${latestDay} days out`}, so that is the most it can show` : `by place-by date, across every SKU the run planned · the last proposed date is ${latestDay} days out`}
                </Text>
            </Group>
            {present.length > 0 && (
                <div className={classes.cards}>
                    {present.map((key) => {
                        const { rows, parties, skus, units, next } = byPivot[key];
                        const late = rows.filter((r) => r.placeBy <= today).length;
                        const party = PIVOT[key].party[parties === 1 ? 0 : 1];
                        return (
                            <div key={key} className={classes.card} data-active={panel === key ? 'true' : undefined} role="button" tabIndex={0} title={panel === key ? 'Click again to go back to the objectives' : `Open ${PIVOT[key].title.toLowerCase()} on the calendar for the next ${withinDays} days`} onClick={() => setPanel(panel === key ? 'objective' : key)}>
                                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                                    {PIVOT[key].title}
                                </Text>
                                <div className={classes.count}>{rows.length}</div>
                                <Text size="xs" c="dimmed">
                                    {rows.length === 0 ? `none in the next ${withinDays} days${next ? ` · next ${dayLabel(next)}` : ''}` : `${skus} SKU${skus === 1 ? '' : 's'} · ${units.toLocaleString('en-IN', { maximumFractionDigits: 0 })} ${key === 'MATERIAL' ? 'qty' : 'units'} · ${parties} ${party}${late ? ` · ${late} due today` : ''}`}
                                </Text>
                            </div>
                        );
                    })}
                </div>
            )}

            {choices > 0 && panel !== 'track' && (
                <Alert color="orange" title={`${choices} line${choices === 1 ? '' : 's'} waiting on a vendor or factory choice`}>
                    No saved answer for this source: answer it once under Settings → Sourcing, or pin a vendor on the line. These cannot be approved until then.
                </Alert>
            )}

            {panel === 'track' && <TrackPanel />}
            {panel !== 'track' && panel !== 'objective' && <CalendarView kind={panel} from={today} to={windowEnd} mode="CALENDAR" onBack={() => setPanel('objective')} />}

            {panel === 'objective' && objectives && onPage.length === 0 && (
                <div className={classes.empty}>
                    <Text fw={600}>Set up replenishment for this brand</Text>
                    <Text size="sm" c="dimmed" mb="sm">
                        Six steps: safety stock and refill, sourcing, the objectives this page answers, who gets scarce material, demand per channel, then run. Until an objective is set the engine does not run on its own.
                    </Text>
                    <Button size="sm" onClick={() => setSettingsOpen(true)}>
                        Start setup
                    </Button>
                </div>
            )}
            {panel === 'objective' && onPage.length > 0 && (
                <Stack gap="sm">
                    {onPage.length > 1 && (
                        <Tabs value={active} onChange={(v) => setActive(v)}>
                            <Tabs.List>
                                {onPage.map((o) => {
                                    const c = counts.find((x) => x.objectiveId === o.id);
                                    return (
                                        <Tabs.Tab
                                            key={o.id}
                                            value={o.id}
                                            rightSection={
                                                c ? (
                                                    <Badge size="xs" variant="light" color={c.dueToday > 0 ? 'red' : 'gray'} tt="none">
                                                        {c.dueToday > 0 ? `${c.dueToday} due · ${c.inPeriod}` : c.inPeriod}
                                                    </Badge>
                                                ) : undefined
                                            }
                                        >
                                            {o.sentence}
                                        </Tabs.Tab>
                                    );
                                })}
                            </Tabs.List>
                        </Tabs>
                    )}
                    {table()}
                </Stack>
            )}

            <SetupWizard opened={settingsOpen} onClose={() => setSettingsOpen(false)} objectives={objectives ?? []} onObjectivesChanged={() => void loadObjectives()} onRan={() => void reloadAll()} />
            {saveReview && (
                <SaveReviewSheet
                    preview={saveReview}
                    objectiveId={current?.id}
                    period={period}
                    onClose={() => setSaveReview(null)}
                    onDone={(result) => {
                        setSaveReview(null);
                        savedNotice(result);
                        void reloadAll();
                    }}
                />
            )}
            <SkuWindow rows={skuDetail} scope="OBJECTIVE" period={period} initialTab={skuTab} onClose={() => setSkuDetail(null)} onApprove={approve} onSave={(ids) => void save(ids)} />
            <MaterialWindow detail={materialDetail} window={{ start: view?.periodStart ?? null, end: view?.periodEnd ?? null }} onClose={() => setMaterialDetail(null)} onApprove={approve} onSave={(ids) => void save(ids)} onOpenSku={(productId) => void openSku(productId)} />
            {cell && <CellWindow kind={cell.kind as 'MATERIALS' | 'TRANSFERS' | 'ORDERS'} row={cell.row} parts={cell.parts} onClose={() => setCell(null)} onApprove={approve} onSave={(ids) => void save(ids)} onChanged={() => void reloadAll()} />}
            <ApprovalSheet ids={approvalIds} objectiveId={current?.id} onClose={() => setApprovalIds(null)} onDone={() => void reloadAll()} />
        </Stack>
    );
};

export default ObjectivesView;
